/**
 * Shared hydrate + normalizers for InlineOpcodeEditor load paths.
 */

import {
    ensureRagStepsInOpcode
} from 'builders/builderOpcode/jsonMode/ragStepWireDefaults';

const VALID_RISK = new Set(['safe', 'moderate', 'sensitive', 'critical']);
const VALID_PII = new Set([
    'email', 'phone', 'name', 'physical_address', 'dob', 'ssn', 'passport',
    'credit_card', 'bank_account', 'ip_address', 'ip_geolocation', 'biometric', 'health', 'custom'
]);
const VALID_COMPLIANCE = new Set([
    'HIPAA', 'PCI-DSS', 'GDPR', 'CCPA', 'SOX', 'FERPA', 'ISO-27001', 'SOC2', 'custom'
]);

export function normalizeInputs(raw) {
    if (!Array.isArray(raw)) return [];
    return raw.map((x) => {
        if (typeof x === 'string') return {
            name: x,
            type: 'string',
            required: true,
            description: ''
        };
        return {
            name: x?.name || '',
            type: x?.type || 'string',
            required: x?.required !== false,
            description: x?.description || '',
            ...(x?.default !== undefined ? {
                default: x.default
            } : {}),
            ...(x?.constraint ? {
                constraint: x.constraint
            } : {})
        };
    }).filter((i) => i.name);
}

export function normalizeOutputs(rawOutputs, legacyOutput) {
    if (Array.isArray(rawOutputs)) {
        return rawOutputs.map((o) => ({
            name: o?.name || '',
            type: o?.type || 'string',
            description: o?.description || '',
            oel_expression: o?.oel_expression || '',
            ...(o?.schema ? {
                schema: o.schema
            } : {})
        })).filter((o) => o.name);
    }
    if (legacyOutput && typeof legacyOutput === 'object') {
        return Object.entries(legacyOutput)
            .filter(([, v]) => typeof v === 'string')
            .map(([k, v]) => ({
                name: k,
                type: 'string',
                description: '',
                oel_expression: v
            }));
    }
    return [];
}

/** Flow.output: TypedOutput[] or legacy map → TypedOutput[]. */
export function normalizeFlowOutput(output) {
    if (Array.isArray(output)) {
        // Server validation requires `type` on every flow output; entries
        // authored outside this editor (JSON mode, older docs) may lack it.
        return output.map((o) => (o && typeof o === 'object' && !o.type
            ? {
                ...o,
                type: 'string'
            }
            : o));
    }
    if (output && typeof output === 'object') {
        return Object.entries(output)
            .filter(([name]) => name)
            .map(([name, oel]) => ({
                name,
                type: 'string',
                oel_expression: typeof oel === 'string' ? oel : (oel == null ? '' : String(oel))
            }));
    }
    return [];
}

/** Returns state patch object from a server opcode document. */
export function hydrateOpcodeFromDoc(d) {
    if (!d) return null;
    const rawRisk = d.risk_level || d.governance_hints?.risk_level;
    const rawPii = Array.isArray(d.pii_handling) ? d.pii_handling : (d.governance_hints?.pii_handling || []);
    const rawCompliance = Array.isArray(d.compliance_tags) ? d.compliance_tags : (d.governance_hints?.compliance_tags || []);
    const loadedSteps = d.steps || [];
    const withRagDefaults = ensureRagStepsInOpcode({
        steps: loadedSteps,
        definitions: d.definitions || [],
        flows: d.flows || []
    });

    return {
        title: d.title || d.name || '',
        description: d.description || '',
        version: d.version || '0.1.0',
        category: d.category || '',
        defaultStepTimeoutMs: typeof d.default_step_timeout_ms === 'number' ? d.default_step_timeout_ms : undefined,
        tags: Array.isArray(d.tags) ? d.tags : [],
        inputs: normalizeInputs(d.inputs),
        outputs: normalizeOutputs(d.outputs, d.output),
        steps: withRagDefaults.steps || loadedSteps,
        definitions: withRagDefaults.definitions || [],
        flows: (withRagDefaults.flows || []).map((f) => ({
            ...f,
            output: normalizeFlowOutput(f.output)
        })),
        hooks: d.hooks && typeof d.hooks === 'object' ? d.hooks : undefined,
        riskLevel: VALID_RISK.has(rawRisk) ? rawRisk : 'moderate',
        requiresApproval: !!(d.requires_approval ?? d.governance_hints?.requires_approval),
        piiHandling: rawPii.filter((v) => VALID_PII.has(v)),
        complianceTags: rawCompliance.filter((v) => VALID_COMPLIANCE.has(v)),
        serverOpcodeId: d.opcode_id,
        schemaVersion: d.schema_version,
        audit: d.audit,
        resourcesUsed: d.resources_used,
        engineCapabilities: d.engine_capabilities_required,
        selectedItem: loadedSteps.length > 0 ? {
            type: 'step',
            idx: 0
        } : null,
    };
}

/** True if list row has enough fields to hydrate editor without fetch. */
export function isOpcodeDocComplete(doc) {
    return doc && doc.opcode_id && Array.isArray(doc.steps);
}
